import { useEffect, useId, useState } from "react"
import { FileText, RotateCcw, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge, Button, Input, Textarea } from "@f2e/ui/components/base"
import type { FormRequestField } from "@f2e/ui/chat/transport"
import { cn } from "@f2e/ui/lib/utils"

import type { AIDraftFormPrefillValue, AIDraftFormProposal } from "@/hooks/api"

export type AIDraftFormPrefilledValues = Record<string, AIDraftFormPrefillValue>

const fieldLabel = (field: FormRequestField) =>
  field.question || field.label || field.name

const fieldOptionLabels = (field: FormRequestField) =>
  (field.options ?? []).flatMap((option: unknown) => {
    if (typeof option === "string" || typeof option === "number") {
      return String(option)
    }
    if (!option || typeof option !== "object") return []
    const { label, value } = option as { label?: unknown; value?: unknown }
    const display = typeof label === "string" && label.trim() ? label : value
    return typeof display === "string" || typeof display === "number"
      ? String(display)
      : []
  })

/** Values the operator may edit: the same field types the server accepts. */
export const prefillableFields = (proposal: AIDraftFormProposal) =>
  proposal.form_schema.fields.filter((field) =>
    proposal.prefillable_field_names.includes(field.name)
  )

export interface AIDraftFormPrefillEditorProps {
  proposal: AIDraftFormProposal
  values: AIDraftFormPrefilledValues
  disabled?: boolean
  onChange: (values: AIDraftFormPrefilledValues) => void
  onRemove: () => void
}

interface NumberPrefillInputProps {
  id: string
  value: number | undefined
  disabled: boolean
  placeholder: string
  onChange: (value: number | undefined) => void
}

const NumberPrefillInput = ({
  id,
  value,
  disabled,
  placeholder,
  onChange,
}: NumberPrefillInputProps) => {
  const [draft, setDraft] = useState(value === undefined ? "" : String(value))

  useEffect(() => {
    setDraft((currentDraft) => {
      const trimmed = currentDraft.trim()
      if (value === undefined && !trimmed) return currentDraft
      const parsed = Number(currentDraft)
      if (
        value !== undefined &&
        trimmed &&
        Number.isFinite(parsed) &&
        parsed === value
      ) {
        return currentDraft
      }
      return value === undefined ? "" : String(value)
    })
  }, [value])

  return (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      value={draft}
      disabled={disabled}
      placeholder={placeholder}
      className="h-8 text-sm"
      onBlur={() => {
        const trimmed = draft.trim()
        if (!trimmed) {
          onChange(undefined)
          return
        }
        const parsed = Number(draft)
        if (Number.isFinite(parsed)) {
          onChange(parsed)
          return
        }
        setDraft(value === undefined ? "" : String(value))
      }}
      onChange={(event) => {
        setDraft(event.target.value)
      }}
    />
  )
}

export const AIDraftFormPrefillEditor = ({
  proposal,
  values,
  disabled = false,
  onChange,
  onRemove,
}: AIDraftFormPrefillEditorProps) => {
  const { t } = useTranslation()
  const fieldIdPrefix = useId()
  const editableFields = prefillableFields(proposal)
  const prefillableNames = new Set(proposal.prefillable_field_names)
  const readOnlyFields = proposal.form_schema.fields.filter(
    (field) => !prefillableNames.has(field.name)
  )

  const setValue = (
    name: string,
    value: AIDraftFormPrefillValue | undefined
  ) => {
    const next = { ...values }
    if (value === undefined) {
      delete next[name]
    } else {
      next[name] = value
    }
    onChange(next)
  }

  return (
    <section
      className="rounded-lg border border-border/80 bg-muted/20"
      aria-label={t("history.aiDraft.form.editorLabel", {
        title: proposal.form_schema.title,
      })}
      data-testid="ai-draft-form-prefill-editor"
    >
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-border/70 px-3 py-2">
        <div className="flex min-w-0 items-start gap-2">
          <FileText
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              {proposal.form_schema.title}
            </div>
            {proposal.form_schema.description && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {proposal.form_schema.description}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="ai-draft-form-pending-badge">
            {t("history.aiDraft.form.notSentYet")}
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={onRemove}
            data-testid="ai-draft-form-remove"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            {t("history.aiDraft.form.remove")}
          </Button>
        </div>
      </header>

      <p className="px-3 pt-2 text-xs text-muted-foreground">
        {t("history.aiDraft.form.sendHelp")}
      </p>

      <div className="space-y-3 px-3 py-3">
        {editableFields.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("history.aiDraft.form.noPrefillableFields")}
          </p>
        ) : (
          editableFields.map((field) => {
            const fieldId = `${fieldIdPrefix}-${field.name}`
            const suggested = proposal.prefilled_values[field.name]
            const current = values[field.name]
            const isCleared = current === undefined
            const canRestore = suggested !== undefined && current !== suggested

            return (
              <div key={field.name} className="space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <label
                    className="text-xs font-medium"
                    htmlFor={fieldId}
                    id={`${fieldId}-label`}
                  >
                    {fieldLabel(field)}
                    {field.required && (
                      <span
                        className="ml-1 text-destructive"
                        aria-label={t("history.aiDraft.form.requiredField")}
                      >
                        *
                      </span>
                    )}
                  </label>
                  <div className="flex items-center gap-1">
                    {canRestore && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-1.5 text-xs"
                        disabled={disabled}
                        onClick={() => setValue(field.name, suggested)}
                        data-testid={`ai-draft-form-restore-${field.name}`}
                      >
                        <RotateCcw className="h-3 w-3" aria-hidden="true" />
                        {t("history.aiDraft.form.restoreSuggestion")}
                      </Button>
                    )}
                    {!isCleared && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-1.5 text-xs"
                        disabled={disabled}
                        onClick={() => setValue(field.name, undefined)}
                        data-testid={`ai-draft-form-clear-${field.name}`}
                      >
                        {t("history.aiDraft.form.clear")}
                      </Button>
                    )}
                  </div>
                </div>
                {field.type === "longText" ? (
                  <Textarea
                    id={fieldId}
                    value={typeof current === "string" ? current : ""}
                    disabled={disabled}
                    placeholder={
                      field.placeholder ??
                      t("history.aiDraft.form.emptyPlaceholder")
                    }
                    className="min-h-16 resize-y text-sm"
                    onChange={(event) =>
                      setValue(field.name, event.target.value)
                    }
                  />
                ) : field.type === "number" ? (
                  <NumberPrefillInput
                    id={fieldId}
                    value={typeof current === "number" ? current : undefined}
                    disabled={disabled}
                    placeholder={
                      field.placeholder ??
                      t("history.aiDraft.form.emptyPlaceholder")
                    }
                    onChange={(value) => setValue(field.name, value)}
                  />
                ) : (
                  <Input
                    id={fieldId}
                    type="text"
                    value={current === undefined ? "" : String(current)}
                    disabled={disabled}
                    placeholder={
                      field.placeholder ??
                      t("history.aiDraft.form.emptyPlaceholder")
                    }
                    className="h-8 text-sm"
                    onChange={(event) =>
                      setValue(field.name, event.target.value)
                    }
                  />
                )}
                {field.helpText && (
                  <p className="text-xs text-muted-foreground">
                    {field.helpText}
                  </p>
                )}
              </div>
            )
          })
        )}

        {readOnlyFields.length > 0 && (
          <div className="border-t border-border/60 pt-3">
            <div className="text-xs font-medium text-muted-foreground">
              {t("history.aiDraft.form.customerFilledFields")}
            </div>
            <ul className="mt-1.5 space-y-1">
              {readOnlyFields.map((field) => {
                const optionLabels = fieldOptionLabels(field)
                return (
                  <li
                    key={field.name}
                    className={cn(
                      "flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground"
                    )}
                  >
                    <span>{fieldLabel(field)}</span>
                    {field.required && (
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    )}
                    {optionLabels.length > 0 && (
                      <span className="text-muted-foreground/80">
                        ({optionLabels.join(" / ")})
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}
